import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  RiFileTextLine,
  RiSearchLine,
  RiBuilding2Line,
  RiUserLine,
  RiCalendarLine,
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiCloseLine,
  RiCheckLine,
  RiInboxLine,
  RiDownload2Line,
  RiSplitCellsHorizontal,
  RiArrowRightLine,
} from "react-icons/ri";
import { generateSplitBill } from "../Util/GenerateSplitBill";
import { generateShortSplitBillPDF } from "../Util/Generateshortsplitbillpdf";
import { LiaRupeeSignSolid } from "react-icons/lia";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const BASE_URL = process.env.REACT_APP_API_URL;

const SHORT_SPLIT_MAX_ROWS = 10;

const Spinner = ({ className = "w-4 h-4" }) => (
  <AiOutlineLoading3Quarters className={`animate-spin ${className}`} />
);

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

// ── numToWords (needed for amountInWords in short PDF) ───────────────────────
const ones = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];
const tens = [
  "",
  "",
  "Twenty",
  "Thirty",
  "Forty",
  "Fifty",
  "Sixty",
  "Seventy",
  "Eighty",
  "Ninety",
];

function numToWords(n) {
  n = Math.round(n);
  if (n === 0) return "Zero";
  if (n < 0) return "Minus " + numToWords(-n);
  if (n < 20) return ones[n];
  if (n < 100)
    return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
  if (n < 1000)
    return (
      ones[Math.floor(n / 100)] +
      " Hundred" +
      (n % 100 ? " " + numToWords(n % 100) : "")
    );
  if (n < 100000)
    return (
      numToWords(Math.floor(n / 1000)) +
      " Thousand" +
      (n % 1000 ? " " + numToWords(n % 1000) : "")
    );
  if (n < 10000000)
    return (
      numToWords(Math.floor(n / 100000)) +
      " Lakh" +
      (n % 100000 ? " " + numToWords(n % 100000) : "")
    );
  return (
    numToWords(Math.floor(n / 10000000)) +
    " Crore" +
    (n % 10000000 ? " " + numToWords(n % 10000000) : "")
  );
}

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
      ${
        type === "success"
          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
          : "bg-red-50 border border-red-200 text-red-800"
      }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, accent }) => (
  <div
    className={`bg-white border rounded-2xl px-5 py-4 flex items-center gap-4 shadow-sm ${accent}`}
  >
    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-emerald-100">
      <Icon className="w-5 h-5 text-emerald-600" />
    </div>
    <div>
      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
        {label}
      </div>
      <div className="text-lg font-extrabold text-slate-800">{value}</div>
    </div>
  </div>
);

const AllSplitbills = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    perPage: 10,
  });
  const [toast, setToast] = useState(null);
  const [splitPdfLoadingId, setSplitPdfLoadingId] = useState(null);

  const navigate = useNavigate();

  const notify = (message, type = "success") => setToast({ message, type });

  const fetchInvoices = async (page = currentPage, searchValue = search) => {
    setLoading(true);

    try {
      const params = {
        page,
      };

      if (searchValue.trim()) {
        params.searchQuery = searchValue.trim();
      }

      const res = await axios.get(`${BASE_URL}/invoice/all`, {
        params,
      });

      const allInvoices = res.data?.data || [];

      setInvoices(
        allInvoices.filter((inv) => inv.splitBillStatus === "complete"),
      );

      setPagination(
        res.data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalRecords: allInvoices.length,
          perPage: 10,
        },
      );
    } catch (err) {
      notify("Failed to load split bills", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchInvoices(currentPage, search);
    }, 400);

    return () => clearTimeout(timer);
  }, [currentPage, search]);

  // ── Count total borrower rows across all splits ───────────────────────────
  // This is the number that drives the short vs. long PDF decision.
  // New structure: each split has a borrowers[] — count those rows.
  // Legacy structure: each split = one borrower row.
  const countTotalRows = (splits) =>
    splits.reduce((total, s) => {
      if (s.borrowers && s.borrowers.length > 0)
        return total + s.borrowers.length;
      return total + 1;
    }, 0);

  // ── Build groups map — handles both new borrowers[] and legacy flat fields ─
  const buildGroups = (splits) => {
    const branchMap = new Map();
    splits.forEach((s) => {
      const key = s.branch?._id || s.branchName || "__none__";
      if (!branchMap.has(key))
        branchMap.set(key, { branchName: s.branchName || "", borrowers: [] });

      const group = branchMap.get(key);

      if (s.borrowers && s.borrowers.length > 0) {
        // New structure
        s.borrowers.forEach((b) => {
          group.borrowers.push({
            borrowersName: b.borrowerName || "",
            perAcc: b.perAccount ?? 0,
            acc: b.accountNumber ?? 1,
            subTotal: b.subtotal ?? 0,
            cgst: b.cgst ?? 0,
            sgst: b.sgst ?? 0,
            total: b.total ?? 0,
          });
        });
      } else {
        // Legacy flat structure
        group.borrowers.push({
          borrowersName: Array.isArray(s.borrowersName)
            ? s.borrowersName.join(", ")
            : s.borrowersName || "",
          perAcc: s.perAcc ?? 0,
          acc: s.acc ?? 1,
          subTotal: s.subTotal ?? 0,
          cgst: s.cgst ?? 0,
          sgst: s.sgst ?? 0,
          total: s.total ?? 0,
        });
      }
    });
    return [...branchMap.values()];
  };
  const handleDownloadSplitBill = async (inv, includeTable = true) => {
    setSplitPdfLoadingId(inv._id);
    try {
      const res = await axios.get(
        `${BASE_URL}/splitsbill/${inv._id}/split-bills`,
      );
      const splits = res.data?.data?.splitBills || [];

      if (!splits.length) {
        notify("No split bills found for this invoice", "error");
        return;
      }

      // ── Build groups map — handles both new borrowers[] and legacy flat structure ──
      const branchMap = new Map();
      splits.forEach((s) => {
        const key = s.branch?._id || s.branchName || "__none__";
        if (!branchMap.has(key))
          branchMap.set(key, { branchName: s.branchName || "", borrowers: [] });

        const group = branchMap.get(key);

        if (s.borrowers && s.borrowers.length > 0) {
          s.borrowers.forEach((b) => {
            group.borrowers.push({
              borrowersName: b.borrowerName || "",
              perAcc: b.perAccount ?? 0,
              acc: b.accountNumber ?? 1,
              subTotal: b.subtotal ?? 0,
              cgst: b.cgst ?? 0,
              sgst: b.sgst ?? 0,
              total: b.total ?? 0,
            });
          });
        } else {
          group.borrowers.push({
            borrowersName: Array.isArray(s.borrowersName)
              ? s.borrowersName.join(", ")
              : s.borrowersName || "",
            perAcc: s.perAcc ?? 0,
            acc: s.acc ?? 1,
            subTotal: s.subTotal ?? 0,
            cgst: s.cgst ?? 0,
            sgst: s.sgst ?? 0,
            total: s.total ?? 0,
          });
        }
      });

      const selectedBank = splits[0]?.selectedBank || null;
      const selectedQuery = splits[0]?.selectedQuery || null;
      const groups = [...branchMap.values()];

      const bankName =
        inv.billTo?.company || inv.releaseOrder?.client?.bankName || "";
      const adType = inv.releaseOrder?.caption || "Branch Wise Split Bills";

      const splitBillType =
        inv.splitBillType || res.data?.data?.splitBillType || "branch";
      const totalBorrowerRows = countTotalRows(splits);

      if (totalBorrowerRows <= SHORT_SPLIT_MAX_ROWS) {
        let fullInv = inv;
        if (!inv.items || inv.items.length === 0) {
          const invRes = await axios.get(`${BASE_URL}/invoice/${inv._id}`);
          fullInv = invRes.data?.data || invRes.data || inv;
        }

        const itemOverridesMap = {};
        if (fullInv.itemEdits && Array.isArray(fullInv.itemEdits)) {
          fullInv.itemEdits.forEach((edit, idx) => {
            itemOverridesMap[idx] = edit;
          });
        }

        const amtWords =
          numToWords(Math.round(fullInv.totalAmount || 0)) + " Only";

        const advertisingTypeName =
          fullInv.advertisingType?.name ||
          inv.advertisingType?.name ||
          fullInv.advertisingType ||
          inv.advertisingType ||
          "";

        const firstDop = fullInv.items?.[0]?.dop || inv.items?.[0]?.dop || "";

        generateShortSplitBillPDF({
          invoiceNumber: fullInv.invoiceNumber || "",
          date: fullInv.date || fullInv.createdAt || "",
          pan: fullInv.pan || "",
          gstNumber: fullInv.gstNumber || "",
          customerId: fullInv.customerId || "",
          hsnCode: fullInv.hsnCode || "",
          advertisingType: advertisingTypeName,
          dop: firstDop,
          splitBillType,
          cgstRate: Number(fullInv.cgstRate || 2.5),
          sgstRate: Number(fullInv.sgstRate || 2.5),
          cgstAmount: Number(fullInv.cgstAmount || 0),
          sgstAmount: Number(fullInv.sgstAmount || 0),
          subTotal: Number(fullInv.subTotal || 0),
          totalAmount: Number(fullInv.totalAmount || 0),
          amountInWords: amtWords,
          billTo: fullInv.billTo || {},
          items: (fullInv.items || []).map((item, idx) => {
            const ov = itemOverridesMap[idx] || {};
            const w = ov.width !== undefined ? ov.width : item.width || 0;
            const h = ov.height !== undefined ? ov.height : item.height || 0;
            return {
              publication: item.publication || "",
              edition: item.edition || "",
              hue: item.hue || "B/W",
              dop: item.dop
                ? new Date(item.dop).toLocaleDateString("en-IN")
                : "",
              page: item.page || item.pagePosition || "",
              width: w,
              height: h,
              volume: w * h,
              rate:
                ov.rate !== undefined
                  ? ov.rate
                  : item.rate || item.ratePerSqCm || 0,
              amount: ov.amount !== undefined ? ov.amount : item.amount || 0,
            };
          }),
          selectedBank,
          selectedQuery,
          groups,
          bankName,
          adType,
          showSplitTable: includeTable,
        });

        notify(
          includeTable
            ? "Split bill PDF (with table) downloaded"
            : "Split bill PDF (without table) downloaded",
        );
      } else {
        generateSplitBill({
          invoiceNumber: inv.invoiceNumber || "",
          date: inv.date || inv.createdAt || "",
          bankName,
          adType,
          cgstRate: inv.cgstRate ?? 2.5,
          sgstRate: inv.sgstRate ?? 2.5,
          subTotal: inv.subTotal || 0,
          totalAmount: inv.totalAmount || 0,
          groups,
          selectedBank,
          selectedQuery,
        });

        notify("Split bill PDF downloaded");
      }
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to generate split bill PDF",
        "error",
      );
    } finally {
      setSplitPdfLoadingId(null);
    }
  };

  const filtered = invoices;

  const totalAmount = filtered.reduce((s, i) => s + (i.totalAmount || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-emerald-400">
          <Spinner className="w-10 h-10" />
          <span className="text-sm font-medium">Loading split bills…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {/* Header */}
      <header className="bg-white border-b border-emerald-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center text-white shadow-md shadow-emerald-200">
              <RiSplitCellsHorizontal className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-emerald-800 tracking-tight">
                Completed Split Bills
              </h1>
              <p className="text-[11px] text-emerald-400 tracking-widest uppercase">
                Invoices with Split Bills Processed
              </p>
            </div>
          </div>

          <div className="relative w-full sm:w-72">
            <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-300" />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search invoice, RO, client…"
              className="w-full pl-9 pr-4 py-2 bg-emerald-50/60 border border-emerald-200 rounded-xl text-sm text-slate-700 placeholder-emerald-300 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition"
            />
            {search && (
              <button
                onClick={() => {
                  setSearch("");
                  setCurrentPage(1);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-300 hover:text-emerald-500 transition"
              >
                <RiCloseLine className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="px-6 py-6 space-y-5 w-full">
        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Completed Split Bills"
            value={invoices.length}
            icon={RiSplitCellsHorizontal}
            accent="border-emerald-100"
          />
          <StatCard
            label="Total Amount"
            value={`₹${fmt(invoices.reduce((s, i) => s + (i.totalAmount || 0), 0))}`}
            icon={LiaRupeeSignSolid}
            accent="border-emerald-100"
          />
          <StatCard
            label="This Month"
            value={
              invoices.filter((i) => {
                const d = new Date(i.date || i.createdAt);
                const now = new Date();
                return (
                  d.getMonth() === now.getMonth() &&
                  d.getFullYear() === now.getFullYear()
                );
              }).length
            }
            icon={RiCalendarLine}
            accent="border-blue-100"
          />
          <StatCard
            label="Showing"
            value={filtered.length}
            icon={RiCheckboxCircleLine}
            accent="border-amber-100"
          />
        </div>

        {/* Table or Empty State */}
        {filtered.length === 0 ? (
          <div className="bg-white border border-emerald-100 rounded-2xl shadow-sm flex flex-col items-center justify-center py-20 gap-3">
            <RiInboxLine className="w-12 h-12 text-emerald-200" />
            <p className="text-sm font-semibold text-slate-400">
              {search
                ? "No completed split bills match your search."
                : "No completed split bills found."}
            </p>
            {search && (
              <button
                onClick={() => setSearch("")}
                className="text-xs text-emerald-500 hover:underline"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white border border-emerald-100 rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-emerald-100 bg-emerald-50/40">
                    {[
                      "#",
                      "Invoice No.",
                      "RO Number",
                      "Bill To",
                      "Date",
                      "Sub Total",
                      "CGST",
                      "SGST",
                      "Total Amount",
                      "Status",
                      "Download",
                      "Action",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-[10px] font-semibold text-emerald-500 uppercase tracking-widest whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((inv, idx) => (
                    <tr
                      key={inv._id}
                      className="border-b border-emerald-50 hover:bg-emerald-50/20 transition group"
                    >
                      <td className="px-4 py-3 text-xs text-slate-400 font-mono">
                        {idx + 1}
                      </td>

                      <td className="px-4 py-3">
                        <span className="font-bold text-emerald-700 font-mono text-xs">
                          {inv.invoiceNumber}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <RiFileTextLine className="w-3.5 h-3.5 text-emerald-300 flex-shrink-0" />
                          <span className="text-xs font-semibold text-slate-700 font-mono">
                            {inv.releaseOrder?.roNumber || "—"}
                          </span>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-start gap-1.5 min-w-0">
                          <RiBuilding2Line className="w-3.5 h-3.5 text-emerald-300 flex-shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <div className="text-xs font-semibold text-slate-700 truncate max-w-[160px]">
                              {inv.billTo?.company ||
                                inv.releaseOrder?.client?.bankName ||
                                "—"}
                            </div>
                            {inv.billTo?.name && (
                              <div className="text-[10px] text-slate-400 flex items-center gap-1">
                                <RiUserLine className="w-3 h-3" />
                                {inv.billTo.name}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                          <RiCalendarLine className="w-3.5 h-3.5 text-emerald-300" />
                          {fmtDate(inv.date || inv.createdAt)}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-xs font-mono text-slate-600 text-left">
                        ₹{fmt(inv.subTotal)}
                      </td>

                      <td className="px-4 py-3 text-xs font-mono text-slate-500 text-left whitespace-nowrap">
                        ₹{fmt(inv.cgstAmount)}
                        <span className="text-[10px] text-slate-400">
                          ({inv.cgstRate}%)
                        </span>
                      </td>

                      <td className="px-4 py-3 text-xs font-mono text-slate-500 text-left whitespace-nowrap">
                        ₹{fmt(inv.sgstAmount)}
                        <span className="text-[10px] text-slate-400">
                          ({inv.sgstRate}%)
                        </span>
                      </td>

                      <td className="px-4 py-3 text-left whitespace-nowrap">
                        <span className="font-extrabold text-emerald-700 font-mono text-sm">
                          ₹{fmt(inv.totalAmount)}
                        </span>
                      </td>

                      {/* Status Badge */}
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
                          <RiCheckboxCircleLine className="w-3 h-3" />
                          Complete
                        </span>
                      </td>

                      {/* Download Button with hover menu */}
                      <td className="px-4 py-3">
                        <div className="relative inline-block group/dl">
                          <button
                            disabled={splitPdfLoadingId === inv._id}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold border bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 transition whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Download Split Bill PDF"
                          >
                            {splitPdfLoadingId === inv._id ? (
                              <Spinner className="w-3.5 h-3.5" />
                            ) : (
                              <RiDownload2Line className="w-3.5 h-3.5" />
                            )}
                            {splitPdfLoadingId === inv._id
                              ? "Generating…"
                              : "Download"}
                          </button>

                          {/* Dropdown — appears on hover, stays open while hovering the menu too */}
                          {splitPdfLoadingId !== inv._id && (
                            <div className="absolute left-0 top-full pt-1 hidden group-hover/dl:block z-20 w-44">
                              <div className="bg-white border border-emerald-200 rounded-lg shadow-lg p-1 flex flex-col gap-0.5">
                                <button
                                  onClick={() =>
                                    handleDownloadSplitBill(inv, true)
                                  }
                                  className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[11px] font-medium text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition text-left"
                                >
                                  <RiDownload2Line className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                                  PDF with Table
                                </button>
                                <button
                                  onClick={() =>
                                    handleDownloadSplitBill(inv, false)
                                  }
                                  className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[11px] font-medium text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition text-left"
                                >
                                  <RiDownload2Line className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                  PDF without Table
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* View Details */}
                      <td className="px-4 py-3">
                        <button
                          onClick={() => {
                            try {
                              navigate(`/admin/split-bill-details/${inv._id}`, {
                                state: {
                                  invoiceData: inv,
                                  splitBills: inv.splitBills || [],
                                },
                              });
                            } catch (error) {
                              console.error("Navigation error:", error);
                              notify(
                                "Failed to navigate to details page",
                                "error",
                              );
                            }
                          }}
                          className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-slate-50 text-slate-500 hover:bg-emerald-600 hover:border-emerald-600 hover:text-white transition-all duration-150 group/arrow"
                          title="View Split Bill Details"
                        >
                          <RiArrowRightLine className="w-4 h-4 transition-transform duration-150 group-hover/arrow:translate-x-0.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>

                <tfoot>
                  <tr className="bg-emerald-50/60 border-t-2 border-emerald-100">
                    <td
                      colSpan={5}
                      className="px-4 py-3 text-xs font-bold text-emerald-700 uppercase tracking-widest"
                    >
                      Total ({filtered.length} invoice
                      {filtered.length !== 1 ? "s" : ""})
                    </td>
                    <td className="px-4 py-3 text-xs font-bold font-mono text-slate-700 text-left">
                      ₹
                      {fmt(filtered.reduce((s, i) => s + (i.subTotal || 0), 0))}
                    </td>
                    <td className="px-4 py-3 text-xs font-bold font-mono text-slate-700 text-left">
                      ₹
                      {fmt(
                        filtered.reduce((s, i) => s + (i.cgstAmount || 0), 0),
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs font-bold font-mono text-slate-700 text-left">
                      ₹
                      {fmt(
                        filtered.reduce((s, i) => s + (i.sgstAmount || 0), 0),
                      )}
                    </td>
                    <td className="px-4 py-3 text-left">
                      <span className="font-extrabold text-emerald-800 font-mono text-base">
                        ₹{fmt(totalAmount)}
                      </span>
                    </td>
                    <td />
                    <td />
                    <td />
                  </tr>
                </tfoot>
              </table>
              <div className="flex items-center justify-between px-6 py-4 border-t bg-white">
                <span className="text-sm text-gray-600">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>

                <div className="flex gap-2">
                  <button
                    disabled={!pagination.hasPrevPage}
                    onClick={() => setCurrentPage((p) => p - 1)}
                    className="px-4 py-2 border rounded disabled:opacity-50"
                  >
                    Previous
                  </button>

                  <button
                    disabled={!pagination.hasNextPage}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    className="px-4 py-2 border rounded disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

export default AllSplitbills;

// import React, { useState, useEffect, useMemo } from "react";
// import axios from "axios";
// import { useNavigate } from "react-router-dom";
// import {
//   RiFileTextLine,
//   RiSearchLine,
//   RiBuilding2Line,
//   RiUserLine,
//   RiCalendarLine,
//   RiCheckboxCircleLine,
//   RiErrorWarningLine,
//   RiCloseLine,
//   RiCheckLine,
//   RiInboxLine,
//   RiDownload2Line,
//   RiSplitCellsHorizontal,
//   RiArrowRightLine,
// } from "react-icons/ri";
// import { generateSplitBill } from "../Util/GenerateSplitBill";
// import { LiaRupeeSignSolid } from "react-icons/lia";
// import { AiOutlineLoading3Quarters } from "react-icons/ai";

// const BASE_URL = process.env.REACT_APP_API_URL;

// const Spinner = ({ className = "w-4 h-4" }) => (
//   <AiOutlineLoading3Quarters className={`animate-spin ${className}`} />
// );

// const fmt = (n) =>
//   Number(n || 0).toLocaleString("en-IN", {
//     minimumFractionDigits: 2,
//     maximumFractionDigits: 2,
//   });

// const fmtDate = (d) =>
//   d
//     ? new Date(d).toLocaleDateString("en-IN", {
//         day: "2-digit",
//         month: "short",
//         year: "numeric",
//       })
//     : "—";

// const Toast = ({ message, type, onClose }) => {
//   useEffect(() => {
//     const t = setTimeout(onClose, 3500);
//     return () => clearTimeout(t);
//   }, [onClose]);
//   return (
//     <div
//       className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
//       ${
//         type === "success"
//           ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
//           : "bg-red-50 border border-red-200 text-red-800"
//       }`}
//     >
//       {type === "success" ? (
//         <RiCheckLine className="w-4 h-4 text-emerald-500" />
//       ) : (
//         <RiErrorWarningLine className="w-4 h-4 text-red-500" />
//       )}
//       <span>{message}</span>
//       <button
//         onClick={onClose}
//         className="ml-2 opacity-50 hover:opacity-100 transition"
//       >
//         <RiCloseLine className="w-4 h-4" />
//       </button>
//     </div>
//   );
// };

// const StatCard = ({ label, value, icon: Icon, accent }) => (
//   <div
//     className={`bg-white border rounded-2xl px-5 py-4 flex items-center gap-4 shadow-sm ${accent}`}
//   >
//     <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-emerald-100">
//       <Icon className="w-5 h-5 text-emerald-600" />
//     </div>
//     <div>
//       <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
//         {label}
//       </div>
//       <div className="text-lg font-extrabold text-slate-800">{value}</div>
//     </div>
//   </div>
// );

// const AllSplitbills = () => {
//   const [invoices, setInvoices] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [search, setSearch] = useState("");
//   const [toast, setToast] = useState(null);
//   const [splitPdfLoadingId, setSplitPdfLoadingId] = useState(null);

//   const navigate = useNavigate();

//   const notify = (message, type = "success") => setToast({ message, type });

//   const fetchInvoices = async () => {
//     setLoading(true);
//     try {
//       const res = await axios.get(`${BASE_URL}/invoice/all`);
//       const allInvoices = res.data?.data || [];
//       setInvoices(
//         allInvoices.filter((inv) => inv.splitBillStatus === "complete"),
//       );
//     } catch {
//       notify("Failed to load split bills", "error");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchInvoices();
//   }, []);

//   const handleDownloadSplitBill = async (inv) => {
//     setSplitPdfLoadingId(inv._id);
//     try {
//       const res = await axios.get(
//         `${BASE_URL}/splitsbill/${inv._id}/split-bills`,
//       );
//       const splits = res.data?.data?.splitBills || [];

//       if (!splits.length) {
//         notify("No split bills found for this invoice", "error");
//         return;
//       }

//       // Build groups supporting both new (borrowers[]) and old (flat) structure
//       const groups = splits.map((s) => {
//         if (s.borrowers && s.borrowers.length > 0) {
//           // New structure — borrowers array
//           return {
//             branchName: s.branchName || "",
//             borrowers: s.borrowers.map((b) => ({
//               borrowersName: b.borrowerName || "",
//               perAcc: b.perAccount || 0,
//               acc: b.accountNumber || 1,
//             })),
//           };
//         }
//         // Old flat structure — backward compatibility
//         return {
//           branchName: s.branchName || "",
//           borrowers: [
//             {
//               borrowersName: Array.isArray(s.borrowersName)
//                 ? s.borrowersName.join(", ")
//                 : s.borrowersName || "",
//               perAcc: s.perAcc || 0,
//               acc: s.acc || 1,
//             },
//           ],
//         };
//       });

//       const selectedBank = splits[0]?.selectedBank || null;
//       const selectedQuery = splits[0]?.selectedQuery || null;

//       generateSplitBill({
//         invoiceNumber: inv.invoiceNumber || "",
//         date: inv.date || inv.createdAt || "",
//         bankName:
//           inv.billTo?.company || inv.releaseOrder?.client?.bankName || "",
//         adType: inv.releaseOrder?.caption || "Branch Wise Split Bills",
//         cgstRate: inv.cgstRate ?? 2.5,
//         sgstRate: inv.sgstRate ?? 2.5,
//         subTotal: inv.subTotal || 0,
//         totalAmount: inv.totalAmount || 0,
//         groups,
//         selectedBank,
//         selectedQuery,
//       });

//       notify("Split bill PDF downloaded successfully");
//     } catch (err) {
//       notify(
//         err?.response?.data?.message || "Failed to generate split bill PDF",
//         "error",
//       );
//     } finally {
//       setSplitPdfLoadingId(null);
//     }
//   };

//   const filtered = useMemo(() => {
//     const q = search.toLowerCase().trim();
//     if (!q) return invoices;
//     return invoices.filter(
//       (inv) =>
//         inv.invoiceNumber?.toLowerCase().includes(q) ||
//         inv.releaseOrder?.roNumber?.toLowerCase().includes(q) ||
//         inv.billTo?.company?.toLowerCase().includes(q) ||
//         inv.billTo?.name?.toLowerCase().includes(q) ||
//         inv.releaseOrder?.client?.bankName?.toLowerCase().includes(q),
//     );
//   }, [invoices, search]);

//   const totalAmount = filtered.reduce((s, i) => s + (i.totalAmount || 0), 0);

//   if (loading) {
//     return (
//       <div className="min-h-screen bg-slate-50 flex items-center justify-center">
//         <div className="flex flex-col items-center gap-4 text-emerald-400">
//           <Spinner className="w-10 h-10" />
//           <span className="text-sm font-medium">Loading split bills…</span>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-screen bg-slate-50 text-slate-800">
//       {/* Header */}
//       <header className="bg-white border-b border-emerald-100 sticky top-0 z-30 shadow-sm">
//         <div className="w-full px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
//           <div className="flex items-center gap-3">
//             <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center text-white shadow-md shadow-emerald-200">
//               <RiSplitCellsHorizontal className="w-5 h-5" />
//             </div>
//             <div>
//               <h1 className="text-base font-bold text-emerald-800 tracking-tight">
//                 Completed Split Bills
//               </h1>
//               <p className="text-[11px] text-emerald-400 tracking-widest uppercase">
//                 Invoices with Split Bills Processed
//               </p>
//             </div>
//           </div>

//           <div className="relative w-full sm:w-72">
//             <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-300" />
//             <input
//               value={search}
//               onChange={(e) => setSearch(e.target.value)}
//               placeholder="Search invoice, RO, client…"
//               className="w-full pl-9 pr-4 py-2 bg-emerald-50/60 border border-emerald-200 rounded-xl text-sm text-slate-700 placeholder-emerald-300 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition"
//             />
//             {search && (
//               <button
//                 onClick={() => setSearch("")}
//                 className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-300 hover:text-emerald-500 transition"
//               >
//                 <RiCloseLine className="w-4 h-4" />
//               </button>
//             )}
//           </div>
//         </div>
//       </header>

//       <div className="px-6 py-6 space-y-5 w-full">
//         {/* Stat Cards */}
//         <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
//           <StatCard
//             label="Completed Split Bills"
//             value={invoices.length}
//             icon={RiSplitCellsHorizontal}
//             accent="border-emerald-100"
//           />
//           <StatCard
//             label="Total Amount"
//             value={`₹${fmt(invoices.reduce((s, i) => s + (i.totalAmount || 0), 0))}`}
//             icon={LiaRupeeSignSolid}
//             accent="border-emerald-100"
//           />
//           <StatCard
//             label="This Month"
//             value={
//               invoices.filter((i) => {
//                 const d = new Date(i.date || i.createdAt);
//                 const now = new Date();
//                 return (
//                   d.getMonth() === now.getMonth() &&
//                   d.getFullYear() === now.getFullYear()
//                 );
//               }).length
//             }
//             icon={RiCalendarLine}
//             accent="border-blue-100"
//           />
//           <StatCard
//             label="Showing"
//             value={filtered.length}
//             icon={RiCheckboxCircleLine}
//             accent="border-amber-100"
//           />
//         </div>

//         {/* Table or Empty State */}
//         {filtered.length === 0 ? (
//           <div className="bg-white border border-emerald-100 rounded-2xl shadow-sm flex flex-col items-center justify-center py-20 gap-3">
//             <RiInboxLine className="w-12 h-12 text-emerald-200" />
//             <p className="text-sm font-semibold text-slate-400">
//               {search
//                 ? "No completed split bills match your search."
//                 : "No completed split bills found."}
//             </p>
//             {search && (
//               <button
//                 onClick={() => setSearch("")}
//                 className="text-xs text-emerald-500 hover:underline"
//               >
//                 Clear search
//               </button>
//             )}
//           </div>
//         ) : (
//           <div className="bg-white border border-emerald-100 rounded-2xl shadow-sm overflow-hidden">
//             <div className="overflow-x-auto">
//               <table className="w-full text-sm">
//                 <thead>
//                   <tr className="border-b border-emerald-100 bg-emerald-50/40">
//                     {[
//                       "#",
//                       "Invoice No.",
//                       "RO Number",
//                       "Bill To",
//                       "Date",
//                       "Sub Total",
//                       "CGST",
//                       "SGST",
//                       "Total Amount",
//                       "Status",
//                       "Download",
//                       "Action",
//                     ].map((h) => (
//                       <th
//                         key={h}
//                         className="px-4 py-3 text-left text-[10px] font-semibold text-emerald-500 uppercase tracking-widest whitespace-nowrap"
//                       >
//                         {h}
//                       </th>
//                     ))}
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {filtered.map((inv, idx) => (
//                     <tr
//                       key={inv._id}
//                       className="border-b border-emerald-50 hover:bg-emerald-50/20 transition group"
//                     >
//                       <td className="px-4 py-3 text-xs text-slate-400 font-mono">
//                         {idx + 1}
//                       </td>

//                       <td className="px-4 py-3">
//                         <span className="font-bold text-emerald-700 font-mono text-xs">
//                           {inv.invoiceNumber}
//                         </span>
//                       </td>

//                       <td className="px-4 py-3">
//                         <div className="flex items-center gap-1.5">
//                           <RiFileTextLine className="w-3.5 h-3.5 text-emerald-300 flex-shrink-0" />
//                           <span className="text-xs font-semibold text-slate-700 font-mono">
//                             {inv.releaseOrder?.roNumber || "—"}
//                           </span>
//                         </div>
//                       </td>

//                       <td className="px-4 py-3">
//                         <div className="flex items-start gap-1.5 min-w-0">
//                           <RiBuilding2Line className="w-3.5 h-3.5 text-emerald-300 flex-shrink-0 mt-0.5" />
//                           <div className="min-w-0">
//                             <div className="text-xs font-semibold text-slate-700 truncate max-w-[160px]">
//                               {inv.billTo?.company ||
//                                 inv.releaseOrder?.client?.bankName ||
//                                 "—"}
//                             </div>
//                             {inv.billTo?.name && (
//                               <div className="text-[10px] text-slate-400 flex items-center gap-1">
//                                 <RiUserLine className="w-3 h-3" />
//                                 {inv.billTo.name}
//                               </div>
//                             )}
//                           </div>
//                         </div>
//                       </td>

//                       <td className="px-4 py-3 whitespace-nowrap">
//                         <div className="flex items-center gap-1.5 text-xs text-slate-500">
//                           <RiCalendarLine className="w-3.5 h-3.5 text-emerald-300" />
//                           {fmtDate(inv.date || inv.createdAt)}
//                         </div>
//                       </td>

//                       <td className="px-4 py-3 text-xs font-mono text-slate-600 text-left">
//                         ₹{fmt(inv.subTotal)}
//                       </td>

//                       <td className="px-4 py-3 text-xs font-mono text-slate-500 text-left whitespace-nowrap">
//                         ₹{fmt(inv.cgstAmount)}
//                         <span className="text-[10px] text-slate-400">
//                           ({inv.cgstRate}%)
//                         </span>
//                       </td>

//                       <td className="px-4 py-3 text-xs font-mono text-slate-500 text-left whitespace-nowrap">
//                         ₹{fmt(inv.sgstAmount)}
//                         <span className="text-[10px] text-slate-400">
//                           ({inv.sgstRate}%)
//                         </span>
//                       </td>

//                       <td className="px-4 py-3 text-left whitespace-nowrap">
//                         <span className="font-extrabold text-emerald-700 font-mono text-sm">
//                           ₹{fmt(inv.totalAmount)}
//                         </span>
//                       </td>

//                       {/* Status Badge */}
//                       <td className="px-4 py-3">
//                         <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
//                           <RiCheckboxCircleLine className="w-3 h-3" />
//                           Complete
//                         </span>
//                       </td>

//                       {/* Download Button */}
//                       <td className="px-4 py-3">
//                         <button
//                           onClick={() => handleDownloadSplitBill(inv)}
//                           disabled={splitPdfLoadingId === inv._id}
//                           className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold border bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 transition whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
//                           title="Download Split Bill PDF"
//                         >
//                           {splitPdfLoadingId === inv._id ? (
//                             <Spinner className="w-3.5 h-3.5" />
//                           ) : (
//                             <RiDownload2Line className="w-3.5 h-3.5" />
//                           )}
//                           {splitPdfLoadingId === inv._id
//                             ? "Generating…"
//                             : "Download"}
//                         </button>
//                       </td>

//                       {/* View Details */}
//                       <td className="px-4 py-3">
//                         <button
//                           onClick={() => {
//                             try {
//                               navigate(`/admin/split-bill-details/${inv._id}`, {
//                                 state: {
//                                   invoiceData: inv,
//                                   splitBills: inv.splitBills || [],
//                                 },
//                               });
//                             } catch (error) {
//                               console.error("Navigation error:", error);
//                               notify(
//                                 "Failed to navigate to details page",
//                                 "error",
//                               );
//                             }
//                           }}
//                           className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-slate-50 text-slate-500 hover:bg-emerald-600 hover:border-emerald-600 hover:text-white transition-all duration-150 group/arrow"
//                           title="View Split Bill Details"
//                         >
//                           <RiArrowRightLine className="w-4 h-4 transition-transform duration-150 group-hover/arrow:translate-x-0.5" />
//                         </button>
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>

//                 <tfoot>
//                   <tr className="bg-emerald-50/60 border-t-2 border-emerald-100">
//                     <td
//                       colSpan={5}
//                       className="px-4 py-3 text-xs font-bold text-emerald-700 uppercase tracking-widest"
//                     >
//                       Total ({filtered.length} invoice
//                       {filtered.length !== 1 ? "s" : ""})
//                     </td>
//                     <td className="px-4 py-3 text-xs font-bold font-mono text-slate-700 text-left">
//                       ₹
//                       {fmt(filtered.reduce((s, i) => s + (i.subTotal || 0), 0))}
//                     </td>
//                     <td className="px-4 py-3 text-xs font-bold font-mono text-slate-700 text-left">
//                       ₹
//                       {fmt(
//                         filtered.reduce((s, i) => s + (i.cgstAmount || 0), 0),
//                       )}
//                     </td>
//                     <td className="px-4 py-3 text-xs font-bold font-mono text-slate-700 text-left">
//                       ₹
//                       {fmt(
//                         filtered.reduce((s, i) => s + (i.sgstAmount || 0), 0),
//                       )}
//                     </td>
//                     <td className="px-4 py-3 text-left">
//                       <span className="font-extrabold text-emerald-800 font-mono text-base">
//                         ₹{fmt(totalAmount)}
//                       </span>
//                     </td>
//                     <td />
//                     <td />
//                     <td />
//                   </tr>
//                 </tfoot>
//               </table>
//             </div>
//           </div>
//         )}
//       </div>

//       {toast && <Toast {...toast} onClose={() => setToast(null)} />}
//     </div>
//   );
// };

// export default AllSplitbills;
